import { branding } from "@creators/backend/lib/config";

/** Support inbox shown on legal pages and App Store listing. */
export const LEGAL_CONTACT_EMAIL = `support@${branding.domain}`;

export type LegalSection = {
  title: string;
  paragraphs: string[];
  bullets?: string[];
};

const updated = "31 July 2026";

export const PRIVACY_META = {
  path: "/privacy",
  title: `Privacy Policy — ${branding.name}`,
  description: `How ${branding.name} collects, uses, and shares personal data for creator collaborations.`,
  updated,
} as const;

export const TERMS_META = {
  path: "/terms",
  title: `Terms of Service — ${branding.name}`,
  description: `Terms for using ${branding.name}: peer-to-peer payments, non-refundable offer boosts, and platform limits.`,
  updated,
} as const;

export const DELETE_ACCOUNT_META = {
  path: "/delete-account",
  title: `Delete your account — ${branding.name}`,
  description: `How to permanently delete your ${branding.name} account and associated personal data from Settings or this page.`,
  updated: "1 August 2026",
} as const;

export const DELETE_ACCOUNT_SECTIONS: LegalSection[] = [
  {
    title: "About this page",
    paragraphs: [
      `This page explains how to permanently delete your ${branding.name} account and what happens to personal data associated with it. ${branding.name} is operated at ${branding.domain} (website and mobile apps).`,
      `Privacy questions (not account deletion): ${LEGAL_CONTACT_EMAIL}.`,
    ],
  },
  {
    title: "How to delete your account",
    paragraphs: [
      "You can permanently delete your account yourself while signed in — no email required:",
    ],
    bullets: [
      "On the website: open Settings → Security, or use the delete control on this page when signed in.",
      "In the iOS or Android app: open Settings → Account → Delete account.",
      "Confirm by typing DELETE. Deletion is immediate and cannot be undone.",
      `If you cannot sign in, email ${LEGAL_CONTACT_EMAIL} from a mailbox you control with the subject “Delete my SendCollab account”, your phone number (with country code), and your public handle if you have one. Typical turnaround for verified requests is within 7 days.`,
    ],
  },
  {
    title: "What is deleted",
    paragraphs: ["When you delete your account, we remove or de-identify personal data tied to it, including where applicable:"],
    bullets: [
      "Account identifiers such as phone number, display name, profile photo, public handle, and bio",
      "Payout details you saved (for example UPI ID or bank account details)",
      "Session and login data for that account",
      "Push notification tokens and similar device identifiers linked to the account",
      "Team memberships and invitations tied to your phone number",
    ],
  },
  {
    title: "What may be retained",
    paragraphs: [
      "Some records may be kept where we have a legitimate need or legal duty, for example fraud prevention, security, accounting, tax, or to preserve the integrity of collaborations that involved another party. Examples:",
    ],
    bullets: [
      "Collaboration history, offers, chat messages, payment proofs, and contracts that the other party still needs for their records — these may be retained or shown in a form that no longer identifies your account where practicable",
      "Transaction references for optional platform fees (Serious / Priority offer boosts) needed for bookkeeping or store/processor obligations",
      "Records we must keep under applicable law",
    ],
  },
  {
    title: "Request deletion of some data (without closing the account)",
    paragraphs: [
      `You can ask us to delete or correct specific personal data while keeping your account open. Email ${LEGAL_CONTACT_EMAIL} and describe what you want removed (for example a profile photo, bio, or payout details).`,
      "You can also update or clear much of this yourself in Settings on the website or in the app (profile photo, display name, bio, and payout fields).",
    ],
  },
  {
    title: "After deletion",
    paragraphs: [
      "Once the account is deleted, you will not be able to sign in with that phone number until you create a new account. Public creator links for a deleted handle may become available for others to claim after deletion is complete.",
      `For more detail on how we handle personal data generally, see our Privacy Policy at https://${branding.domain}/privacy.`,
    ],
  },
];

export const PRIVACY_SECTIONS: LegalSection[] = [
  {
    title: "Who we are",
    paragraphs: [
      `${branding.name} (“we”, “us”) operates the website at ${branding.domain} and the ${branding.name} mobile apps. This Privacy Policy explains what personal data we collect when you use the service, how we use it, and the choices you have.`,
      `Contact for privacy questions: ${LEGAL_CONTACT_EMAIL}.`,
    ],
  },
  {
    title: "What we collect",
    paragraphs: [
      "Depending on how you use the service, we may collect:",
    ],
    bullets: [
      "Account data: phone number, display name, profile photo, public handle, and bio.",
      "Collaboration content: offers, counters, written terms, chat messages, photos, voice notes, call recordings on Priority collabs (where enabled), and payment proofs you upload.",
      "Payout details you choose to share: UPI ID, bank account details, or other payout instructions shown to a collaborator when payment is due.",
      "Transaction metadata for optional platform fees (Serious / Priority offer boosts), including app-store or payment-provider references.",
      "Device and usage data: app version, approximate logs needed to operate and secure the service, and crash or diagnostic data if enabled.",
      "Communications you send to support.",
    ],
  },
  {
    title: "How we use data",
    paragraphs: ["We use personal data to:"],
    bullets: [
      "Create and secure your account (including one-time passcodes).",
      "Run collaborations: offers, negotiation, contracts, deadlines, chat, and payment confirmation records.",
      "Show payout instructions only to a party who owes money on that collab, when the product flow requires it.",
      "Process optional platform fees for Serious and Priority offers.",
      "Send transactional notices (for example WhatsApp or in-app alerts about offers and deadlines).",
      "Prevent abuse, enforce our Terms, and comply with law.",
      "Improve reliability and fix bugs.",
    ],
  },
  {
    title: "How we share data",
    paragraphs: [
      "We do not sell your personal data. We share it only as needed to run the product:",
    ],
    bullets: [
      "With the other party on a collab you participate in (offers, terms, chat, proofs, and payout details when payment is due).",
      "With service providers who process data for us (hosting, databases, messaging, analytics/diagnostics, and payment or app-store processors for platform fees).",
      "When required by law, legal process, or to protect rights, safety, and the integrity of the service.",
      "In connection with a merger, acquisition, or asset sale, subject to appropriate protections.",
    ],
  },
  {
    title: "Peer-to-peer payments",
    paragraphs: [
      `${branding.name} is not an escrow service and does not hold collaboration fees. Money for collabs moves directly between creator and collaborator (for example via UPI or bank transfer). We may store references, screenshots, and confirmations that you or the other party submit; we cannot see your bank balance and we do not control those transfers.`,
    ],
  },
  {
    title: "Retention",
    paragraphs: [
      "We keep account and collab records for as long as your account is active and as needed to provide the service, resolve disputes, enforce agreements, and meet legal or accounting obligations. You can permanently delete your account in Settings or at the delete-account page while signed in; some records may be retained where we have a legitimate need or legal duty to keep them (for example fraud prevention or tax).",
    ],
  },
  {
    title: "Security",
    paragraphs: [
      "We use reasonable technical and organisational measures to protect personal data (including HTTPS in transit and access controls). No method of transmission or storage is perfectly secure; you use the service at your own risk regarding residual security risk.",
    ],
  },
  {
    title: "Your choices",
    paragraphs: [
      `You can update profile and payout settings in the app or website. You can permanently delete your account in Settings (website or app) or at https://${branding.domain}/delete-account while signed in. You can also ask us to access, correct, or delete personal data we hold about you, subject to applicable law, by emailing the address above. Marketing messages, if any, can be opted out of where offered; transactional messages about your collabs may still be sent while your account is open.`,
    ],
  },
  {
    title: "Children",
    paragraphs: [
      `The service is not directed to children under 13 (or the minimum age required in your country). If you believe a child has provided us personal data, contact ${LEGAL_CONTACT_EMAIL} and we will take appropriate steps.`,
    ],
  },
  {
    title: "International transfers",
    paragraphs: [
      "We may process data on servers or with providers outside your country. Where we do, we take steps appropriate to the context to protect the data.",
    ],
  },
  {
    title: "Changes",
    paragraphs: [
      "We may update this Privacy Policy from time to time. The “Last updated” date at the top of the page will change when we do. Continued use of the service after an update means you accept the revised policy, except where applicable law requires a different process.",
    ],
  },
];

export const TERMS_SECTIONS: LegalSection[] = [
  {
    title: "Agreement",
    paragraphs: [
      `These Terms of Service (“Terms”) govern your use of ${branding.name}’s website, apps, and related services (the “Service”). By creating an account or using the Service, you agree to these Terms and to our Privacy Policy at https://${branding.domain}/privacy.`,
      `If you do not agree, do not use the Service. Contact: ${LEGAL_CONTACT_EMAIL}.`,
    ],
  },
  {
    title: "What the Service is",
    paragraphs: [
      `${branding.name} is a tool that helps creators and collaborators run paid (or barter) collaborations: a public offer link, structured negotiation, written terms, chat, deadlines, and records of payment claims and confirmations.`,
      "The Service is not a marketplace, talent agency, escrow provider, bank, payment gateway for collaboration fees, or dispute-resolution tribunal. There is no discovery or matching of strangers by us — you bring your own counterparties.",
    ],
  },
  {
    title: "Your account",
    paragraphs: [
      "You must provide accurate information and keep your login credentials secure. You are responsible for activity under your account. You must be old enough to form a binding contract in your jurisdiction (and at least 13). We may suspend or terminate accounts that violate these Terms or create risk for other users or the Service.",
    ],
  },
  {
    title: "Collaborations are between you and the other party",
    paragraphs: [
      "Every collaboration is a private arrangement between the creator and the collaborator (and any teammates they invite). When terms are accepted, that agreement is between those parties. We are not a party to that agreement, we do not guarantee either side will perform, and we do not guarantee that any payment will be sent, received, or truthful.",
    ],
    bullets: [
      "You are solely responsible for evaluating the other party, the brief, pricing, deliverables, and legal compliance of the work.",
      "Disputes about quality, deadlines, refunds of collab fees, chargebacks on peer-to-peer transfers, fraud, or non-payment must be resolved between the parties. We are not obliged to mediate, investigate, or pay either side.",
      "Records, screenshots, and “confirmed” payment states in the Service are convenience features based on what users submit. They are not bank verification and do not make us liable for money movement.",
    ],
  },
  {
    title: "No escrow — we never hold collaboration money",
    paragraphs: [
      "Collaboration fees are paid directly between users (for example UPI or bank transfer). We do not hold, receive, transmit, or custody those funds. We do not take a percentage of collaboration payments. Because we never hold the money, we cannot reverse, refund, freeze, or recover collaboration payments for you.",
    ],
  },
  {
    title: "Platform fees (Serious / Priority) — no refunds",
    paragraphs: [
      "Sending a Basic offer may be free. Optional Serious and Priority fees are platform charges a collaborator pays so an offer is ranked or featured (for example primary inbox placement, notifications, call option, or read receipts), as described in the product at the time of purchase. Those fees are not a deposit toward the creator’s collaboration fee and are not held for either party.",
      "Except where mandatory consumer law requires otherwise, or where the app store or payment processor that collected the fee independently issues a refund under its own rules, Serious and Priority (and any similar platform) fees are final and non-refundable. No refund is due if:",
    ],
    bullets: [
      "The creator does not accept, reply to, or complete the offer or collab.",
      "The other party is slow, quiet, or unprofessional.",
      "You change your mind, sent the offer by mistake, or negotiated different terms.",
      "The collab is cancelled, expires, lapses, or ends in a dispute.",
      "You are unhappy with placement, notifications, or outcomes after purchase.",
    ],
  },
  {
    title: "App Store and Google Play purchases",
    paragraphs: [
      "If you buy a platform fee through Apple App Store or Google Play, that purchase is also subject to the store’s terms. Refund requests for store-billed purchases may need to be made to Apple or Google under their policies. A store refund, if any, does not create extra obligations on us beyond what the store requires.",
    ],
  },
  {
    title: "User content and acceptable use",
    paragraphs: [
      "You retain rights in content you submit. You grant us a worldwide, non-exclusive licence to host, process, transmit, and display that content as needed to operate the Service.",
      "You must not use the Service to harass, defraud, infringe IP, send spam, upload illegal or harmful material, probe or disrupt systems, scrape at scale, or bypass fees or access controls. We may remove content or restrict features when we reasonably believe these Terms or law are violated.",
    ],
  },
  {
    title: "Calls and recordings",
    paragraphs: [
      "On features that bridge phone calls (for example Priority collabs), calls may be recorded and posted into the collab thread as described in the product. You are responsible for complying with recording and privacy laws that apply to you and the other party.",
    ],
  },
  {
    title: "Third-party services",
    paragraphs: [
      "The Service may rely on third parties (hosting, messaging, UPI apps, app stores, payment processors for platform fees). Their terms and availability are outside our control. Failures of those services do not make us liable for missed payments or lost deals between users.",
    ],
  },
  {
    title: "Disclaimer of warranties",
    paragraphs: [
      `THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE”. TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT ANY COLLABORATION WILL BE SUCCESSFUL OR PAID.`,
    ],
  },
  {
    title: "Limitation of liability",
    paragraphs: [
      `TO THE MAXIMUM EXTENT PERMITTED BY LAW, ${branding.name.toUpperCase()} AND ITS OPERATORS WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, LOST REVENUE, LOST DATA, OR BUSINESS INTERRUPTION, ARISING FROM YOUR USE OF THE SERVICE OR FROM ANY COLLABORATION OR PEER-TO-PEER PAYMENT.`,
      "TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY FOR ANY CLAIM RELATING TO THE SERVICE IS LIMITED TO THE GREATER OF (A) THE PLATFORM FEES YOU PAID TO US FOR THE FEATURE GIVING RISE TO THE CLAIM IN THE THREE MONTHS BEFORE THE CLAIM, OR (B) INR 1,000. These limits do not apply where liability cannot be limited under applicable law.",
    ],
  },
  {
    title: "Indemnity",
    paragraphs: [
      `You will indemnify and hold harmless ${branding.name} and its operators from claims, damages, losses, and expenses (including reasonable legal fees) arising out of your content, your collaborations, your peer-to-peer payments, or your breach of these Terms or applicable law.`,
    ],
  },
  {
    title: "Changes and termination",
    paragraphs: [
      "We may change the Service or these Terms. Material changes will be reflected by updating the “Last updated” date (and by notice in-product when we consider it appropriate). Continued use after changes become effective constitutes acceptance, except where law requires otherwise. We may stop offering the Service or your access to it at any time, including for violations of these Terms.",
    ],
  },
  {
    title: "Governing law",
    paragraphs: [
      "These Terms are governed by the laws of India, without regard to conflict-of-law rules. Courts in India will have exclusive jurisdiction, except where mandatory consumer protections in your country give you non-waivable rights to sue elsewhere.",
    ],
  },
  {
    title: "Contact",
    paragraphs: [`Questions about these Terms: ${LEGAL_CONTACT_EMAIL}.`],
  },
];
